import { Box, Toolbar as MuiToolbar, useTheme } from '@mui/material';
import type { ReactElement } from 'react';

import InputSearch from '../../../InputSearch/InputSearch';
import { LoadingSpinner } from '../../../LoadingSpinner/LoadingSpinner';
import type { Column } from '../../TableDataModel';
import type { ReadableSet } from '../../useApiTableData';
import { type ExportOpts, type PrintOpts, ToolbarActions } from './ToolbarActions';

type ToolbarProps<TRow extends { id: string }> = {
  initialSearchString: string;
  onSearch: (arg0: string) => void;
  allColumns: ReadableSet<Column<TRow>>;
  columnsFiltersCount: number;
  onColumnsFilterIconClick: () => void;
  renderedColumns: ReadableSet<Column<TRow>>;
  isFetchingRows: boolean;
  printOpts?: PrintOpts;
  exportOpts?: ExportOpts<TRow>;
  onColumnChange: (columnID: string, isRendered: boolean) => void;
  onColumnReorder: (newColumns: Column<TRow>[]) => void;
  toolbarFiltersSlot?: ReactElement;
  tableName?: string;
  onSave: () => void;
  onClear: () => void;
};

export const Toolbar = <TRow extends { id: string }>({
  initialSearchString,
  onSearch,
  allColumns: allColumnsSet,
  renderedColumns,
  isFetchingRows,
  printOpts,
  exportOpts,
  onColumnChange,
  onColumnReorder,
  toolbarFiltersSlot,
  tableName,
  columnsFiltersCount = 0,
  onColumnsFilterIconClick,
  onSave,
  onClear,
}: ToolbarProps<TRow>): ReactElement => {
  const theme = useTheme();

  return (
    <Box pb='1.25rem' bgcolor={theme.palette.SlabGray['50']}>
      <MuiToolbar
        sx={{
          flexWrap: 'wrap',
          gap: '1rem',
          borderRadius: '0.625rem',
          bgcolor: theme.palette.SlabGray[0],
          justifyContent: 'space-between',
          padding: '1rem',
          boxShadow: theme.shadows[1],

          [theme.breakpoints.down('lg')]: {
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
          },
        }}
      >
        <InputSearch
          initialValue={initialSearchString}
          onChange={(v): void => onSearch(v)}
          sx={{
            [theme.breakpoints.down('xl')]: {
              width: '100%',
            },
          }}
        />

        <Box
          flex={1}
          display='flex'
          gap='1rem'
          justifyContent='flex-end'
          sx={{
            [theme.breakpoints.down('lg')]: {
              flexWrap: 'wrap',
              width: '100%',
            },
          }}
        >
          {isFetchingRows && (
            <LoadingSpinner
              color='inherit'
              size={24}
              sx={{ margin: 'auto 8px' }}
              wrapInBox={false}
            />
          )}
          {toolbarFiltersSlot}
        </Box>

        <ToolbarActions
          allColumns={allColumnsSet}
          renderedColumns={renderedColumns}
          onColumnChange={onColumnChange}
          onColumnReorder={onColumnReorder}
          columnsFiltersCount={columnsFiltersCount}
          onColumnsFilterIconClick={onColumnsFilterIconClick}
          {...(tableName !== undefined && { onSave, onClear })}
          printOpts={printOpts}
          exportOpts={exportOpts}
        />
      </MuiToolbar>
    </Box>
  );
};
